import { Link } from "@tanstack/react-router";
import { useMe } from "@/features/auth/useMe";
import { ADMIN_GROUPS, ADMIN_SECTIONS, canSeeAdminSection } from "../nav";
import type { AdminSectionId } from "../nav";

/**
 * M8a F2 — three-group sidebar (Broadcast / Tenants / Platform).
 * Items hidden per `canSeeAdminSection` so an uploader sees only
 * Matches, a team admin sees Matches + People, a global admin sees
 * everything.
 *
 * The active section is highlighted via `data-active`; the data
 * attribute lets E2E + screenshot tests assert without scraping
 * className strings.
 */
export function AdminSidebar({
  active,
}: {
  active: AdminSectionId;
}) {
  const me = useMe().data;

  return (
    <aside
      data-testid="admin-sidebar"
      className="w-full shrink-0 border-b border-rule bg-surface/40 px-2 py-2 md:w-56 md:border-b-0 md:border-r md:py-3"
    >
      <nav className="flex gap-3 overflow-x-auto pb-1 md:flex-col md:gap-4 md:overflow-visible md:pb-0">
        {ADMIN_GROUPS.map((group) => {
          const items = ADMIN_SECTIONS.filter(
            (s) => s.group === group.id && canSeeAdminSection(s.id, me),
          );
          if (items.length === 0) return null;

          return (
            <div
              key={group.id}
              className="min-w-max md:min-w-0"
              data-admin-group={group.id}
            >
              <h3 className="px-2 mb-1 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                {group.label}
              </h3>
              <ul className="flex gap-1 md:flex-col md:gap-0.5">
                {items.map((s) => (
                  <li key={s.id}>
                    <Link
                      to={s.path}
                      data-testid={`admin-nav-${s.id}`}
                      data-admin-section={s.id}
                      data-active={s.id === active ? "true" : undefined}
                      className="group flex min-w-[8.5rem] items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-bg-card transition-colors data-[active=true]:bg-bg-card data-[active=true]:text-ink text-ink-muted md:min-w-0"
                    >
                      <span className="text-[0.6rem] font-mono text-ink-muted/60 group-hover:text-ink-muted">
                        {s.glyph}
                      </span>
                      <span className="flex flex-col leading-tight">
                        <span className="text-sm">{s.label}</span>
                        <span className="text-[0.6rem] text-ink-muted/70 group-hover:text-ink-muted">
                          {s.kicker}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
